import {
  overrideForLine,
  resolveSpanishLine,
  SPANISH_CHROME,
  translationSourceHash,
  type FormSpanishOverrides,
  type FieldSpanishOverrides,
  type SpanishLine,
  type SpanishOverride,
  type SpanishSourceField,
  type SpanishSourceForm,
  collectSpanishLines,
  normalizeTranslatableText,
  settingsRecord,
} from "./spanish";

export function missingSpanishLines(input: {
  form: SpanishSourceForm;
  overrides: FormSpanishOverrides;
  cacheByHash: ReadonlyMap<string, string> | Record<string, string>;
}): SpanishLine[] {
  const cache =
    input.cacheByHash instanceof Map
      ? input.cacheByHash
      : new Map(Object.entries(input.cacheByHash));
  return collectSpanishLines(input.form).filter((entry) => {
    const resolved = resolveSpanishLine({
      english: entry.english,
      override: overrideForLine(input.overrides, entry.key),
      cacheText: cache.get(entry.hash),
    });
    return resolved.origin === "missing";
  });
}

function translated(input: {
  english: string;
  override?: SpanishOverride | null;
  cacheByHash: ReadonlyMap<string, string>;
}): string {
  const resolved = resolveSpanishLine({
    english: input.english,
    override: input.override,
    cacheText: input.cacheByHash.get(translationSourceHash(input.english)),
  });
  return (
    resolved.text ??
    SPANISH_CHROME[normalizeTranslatableText(input.english)] ??
    input.english
  );
}

export function applySpanishToForm<T extends SpanishSourceForm>(
  form: T,
  overrides: FormSpanishOverrides,
  cacheByHash: ReadonlyMap<string, string> | Record<string, string>,
): T {
  const cache =
    cacheByHash instanceof Map
      ? cacheByHash
      : new Map(Object.entries(cacheByHash));
  return {
    ...form,
    title: translated({
      english: form.title,
      override: overrides.title,
      cacheByHash: cache,
    }),
    description: form.description
      ? translated({
          english: form.description,
          override: overrides.description,
          cacheByHash: cache,
        })
      : form.description,
    sections: form.sections.map((section) => ({
      ...section,
      name: translated({
        english: section.name,
        override: overrides.sections?.[section.id]?.name,
        cacheByHash: cache,
      }),
      description: section.description
        ? translated({
            english: section.description,
            override: overrides.sections?.[section.id]?.description,
            cacheByHash: cache,
          })
        : section.description,
      fields: section.fields.map((field) =>
        applyField(field, overrides.fields?.[field.id], cache),
      ),
    })),
  };
}

function applyField<T extends SpanishSourceField>(
  field: T,
  overrides: FieldSpanishOverrides | undefined,
  cache: ReadonlyMap<string, string>,
): T {
  const settings = settingsRecord(field.settings);
  const nextSettings = { ...settings };
  if (typeof settings.placeholder === "string") {
    nextSettings.placeholder = translated({
      english: settings.placeholder,
      override: overrides?.placeholder,
      cacheByHash: cache,
    });
  }
  if (typeof settings.yesLabel === "string") {
    nextSettings.yesLabel = translated({
      english: settings.yesLabel,
      override: overrides?.yesLabel,
      cacheByHash: cache,
    });
  }
  if (typeof settings.noLabel === "string") {
    nextSettings.noLabel = translated({
      english: settings.noLabel,
      override: overrides?.noLabel,
      cacheByHash: cache,
    });
  }
  if (Array.isArray(settings.bullets)) {
    nextSettings.bullets = settings.bullets.map((bullet, index) =>
      typeof bullet === "string"
        ? translated({
            english: bullet,
            override: overrides?.bullets?.[index],
            cacheByHash: cache,
          })
        : bullet,
    );
  }
  const options = Array.isArray(field.options)
    ? field.options.map((option, index) => {
        if (typeof option === "string") {
          return translated({
            english: option,
            override: overrides?.options?.[`option_${index}`],
            cacheByHash: cache,
          });
        }
        if (!option || typeof option !== "object") return option;
        const record = option as Record<string, unknown>;
        if (typeof record.label !== "string") return option;
        const id =
          typeof record.id === "string" && record.id
            ? record.id
            : `option_${index}`;
        return {
          ...record,
          label: translated({
            english: record.label,
            override: overrides?.options?.[id],
            cacheByHash: cache,
          }),
        };
      })
    : field.options;
  return {
    ...field,
    label: translated({
      english: field.label,
      override: overrides?.label,
      cacheByHash: cache,
    }),
    options,
    settings: nextSettings,
  };
}

type DefinitionLike = {
  title: string;
  description?: string | null;
  steps: Array<{
    title: string;
    description?: string | null;
    fields: Array<Record<string, unknown>>;
  }>;
};

export function applySpanishToDefinition<T extends DefinitionLike>(
  definition: T,
  overrides: FormSpanishOverrides,
  cacheByHash: ReadonlyMap<string, string> | Record<string, string>,
): T {
  const cache =
    cacheByHash instanceof Map
      ? cacheByHash
      : new Map(Object.entries(cacheByHash));
  return {
    ...definition,
    title: translatePlain(definition.title, cache),
    description: definition.description
      ? translatePlain(definition.description, cache)
      : definition.description,
    steps: definition.steps.map((step) => ({
      ...step,
      title: translatePlain(step.title, cache),
      description: step.description
        ? translatePlain(step.description, cache)
        : step.description,
      fields: step.fields.map((field) =>
        translateDefinitionField(field, overrides, cache),
      ),
    })),
  };
}

function translatePlain(
  english: string,
  cache: ReadonlyMap<string, string>,
): string {
  return SPANISH_CHROME[english] ?? translated({ english, cacheByHash: cache });
}

function translateDefinitionField(
  field: Record<string, unknown>,
  overrides: FormSpanishOverrides,
  cache: ReadonlyMap<string, string>,
): Record<string, unknown> {
  const id = typeof field.id === "string" ? field.id : "";
  const fieldOverrides = overrides.fields?.[id];
  const next = { ...field };
  if (typeof field.description === "string")
    next.description = translatePlain(field.description, cache);
  if (typeof field.label === "string") {
    next.label = translated({
      english: field.label,
      override: fieldOverrides?.label,
      cacheByHash: cache,
    });
  }
  if (typeof field.placeholder === "string") {
    next.placeholder = translated({
      english: field.placeholder,
      override: fieldOverrides?.placeholder,
      cacheByHash: cache,
    });
  }
  if (typeof field.yesLabel === "string") {
    next.yesLabel = translated({
      english: field.yesLabel,
      override: fieldOverrides?.yesLabel,
      cacheByHash: cache,
    });
  }
  if (typeof field.noLabel === "string") {
    next.noLabel = translated({
      english: field.noLabel,
      override: fieldOverrides?.noLabel,
      cacheByHash: cache,
    });
  }
  if (typeof field.body === "string") {
    next.body = translatePlain(field.body, cache);
  }
  if (Array.isArray(field.bullets)) {
    next.bullets = field.bullets.map((bullet, index) =>
      typeof bullet === "string"
        ? translated({
            english: bullet,
            override: fieldOverrides?.bullets?.[index],
            cacheByHash: cache,
          })
        : bullet,
    );
  }
  if (Array.isArray(field.options)) {
    next.options = field.options.map((option) => {
      if (!option || typeof option !== "object") return option;
      const record = option as Record<string, unknown>;
      if (typeof record.label !== "string") return option;
      const optionId = typeof record.id === "string" ? record.id : "";
      return {
        ...record,
        label: translated({
          english: record.label,
          override: optionId ? fieldOverrides?.options?.[optionId] : null,
          cacheByHash: cache,
        }),
      };
    });
  }
  if (Array.isArray(field.fields)) {
    next.fields = field.fields.map((child) =>
      child && typeof child === "object"
        ? translateDefinitionField(
            child as Record<string, unknown>,
            overrides,
            cache,
          )
        : child,
    );
  }
  return next;
}
